import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";

/**
 * /forgot-password
 *
 * Step 1 of the Supabase password-reset flow.
 *
 *   • User enters their email.
 *   • We call `supabase.auth.resetPasswordForEmail(email, { redirectTo })`
 *     with redirectTo computed from `window.location.origin` so the same
 *     code works across localhost, inonit.vercel.app, inonit.app, and
 *     www.inonit.app — provided each origin is allow-listed in the
 *     Supabase dashboard (Auth → URL Configuration → Redirect URLs).
 *
 * For privacy reasons we always show a generic "if an account exists" toast
 * instead of leaking whether the address is registered.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: err } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo,
      });
      if (err) {
        // Rate-limit + config errors: surface with a generic message so we
        // never leak account-existence information.
        // eslint-disable-next-line no-console
        console.warn("forgot-password: supabase error", err);
        setError("Couldn't send the reset email. Please try again in a minute.");
      } else {
        setSent(true);
        toast.success("Reset link sent. Check your inbox.");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("forgot-password: unexpected", err);
      setError("Couldn't send the reset email. Please try again in a minute.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-ink/15 bg-grain">
        <Link to="/" className="inline-flex items-center">
          <img src="/inonit-logo.png" alt="inonit" className="h-9 w-auto select-none" draggable={false} />
        </Link>
        <div>
          <p className="font-display text-4xl xl:text-6xl font-bold leading-[0.95] tracking-tight text-ink">
            Forgot your<br />password?<br />
            <span className="text-ink/55">Happens to all of us.</span>
          </p>
          <p className="text-ink/60 mt-6 max-w-md">
            We'll email you a fresh link to set a new one.
          </p>
        </div>
        <span className="text-xs uppercase tracking-widest text-ink/40">© inonit</span>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md" data-testid="forgot-password-card">
          <span className="text-xs uppercase tracking-[0.3em] text-ink">Reset password</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 mb-10 leading-none">
            {sent ? "Check your inbox." : "Forgot it.\nNo stress."}
          </h1>

          {sent ? (
            <div data-testid="forgot-success-state" className="flex flex-col gap-6">
              <div className="border-[2.5px] border-ink rounded-2xl p-5 bg-paper flex items-start gap-3">
                <span className="shrink-0 w-10 h-10 rounded-full bg-neon border-[2.5px] border-ink inline-flex items-center justify-center">
                  <MailCheck size={18} strokeWidth={2.6} />
                </span>
                <div className="text-sm text-ink/80 leading-relaxed">
                  Password reset link sent to <span className="font-bold text-ink">{email}</span>.
                  Check your inbox (and spam, just in case). The link is valid for one hour.
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-send-another"
                className="text-sm text-ink/60 hover:text-ink underline-offset-4 hover:underline transition-colors self-start"
              >
                Send to a different email
              </button>
              <Link
                to="/login"
                data-testid="forgot-back-to-login"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink hover:opacity-70 transition-opacity"
              >
                <ArrowLeft size={14} strokeWidth={2.6} /> Back to log in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-password-form" className="flex flex-col gap-6">
              <label className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-widest text-ink/60">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  placeholder="you@yourtown.com"
                  className="chunky-input"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoFocus
                />
              </label>

              {error && (
                <div data-testid="forgot-error" className="text-destructive text-sm border border-destructive/40 px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                data-testid="forgot-submit"
                className="bg-neon text-ink py-4 font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60 mt-2"
              >
                {loading ? "Sending link…" : "Send reset link"}
              </button>

              <Link
                to="/login"
                data-testid="forgot-back-to-login"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors self-start"
              >
                <ArrowLeft size={14} strokeWidth={2.6} /> Back to log in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
